import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  TablePagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import axiosInstance from "../../../../services/axiosInstance";
import { CHANGE_NICKNAME_BROADBAND_Url } from "../../../../const/URL";
import { getCircuitsByUserIDV2 } from "../../../../store/actions/dashborad.action";
import { ICustomerCircuit } from "../../../../interfaces/customerCircuid";
import MainLoader from "../../../../components/loading/MainLoader";

interface ApiResponse {
  dataBundle: ICustomerCircuit[];
  isSuccess: boolean;
  errorMessage: string | null;
  successMessage: string | null;
}

const BroadbandFavourites: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [data, setData] = useState<ICustomerCircuit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [filterAccNo, setFilterAccNo] = useState<string>("");
  const [filterNickname, setFilterNickname] = useState<string>("");
  const [selectedAccount, setSelectedAccount] =
    useState<ICustomerCircuit | null>(null);
  const [editedNickname, setEditedNickname] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  useEffect(() => {
    fetchBroadBandNumbers();
  }, []);

  const customerId = localStorage.getItem("CustomerID");

  const id = Number(customerId);

  const fetchBroadBandNumbers = async () => {
    try {
      setLoading(true);
      const response = await getCircuitsByUserIDV2(id);

      if (response) {
        setData(response.data);
      } else {
        throw new Error("Invalid API response format");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (account: ICustomerCircuit) => {
    setSelectedAccount(account);
    setEditedNickname(account.circuitNickName);
    setOpenDialog(true);
  };

  const handleSaveNickname = async () => {
    if (!selectedAccount) return;

    try {
      const payload = {
        circuitNickName: editedNickname,
      };

      const response = await axiosInstance.put<ApiResponse>(
        `${CHANGE_NICKNAME_BROADBAND_Url}/${selectedAccount.circuitId}`,
        payload
      );

      if (response.data.isSuccess) {
        setSuccess(true);
        fetchBroadBandNumbers();
        setOpenDialog(false);
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to save nickname"
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save nickname");
    }
  };

  const filteredData = data.filter((item) => {
    const accNoMatch = item.circuitId
      ?.toString()
      ?.toLowerCase()
      ?.includes(filterAccNo.toLowerCase());
    const nickname = item?.circuitNickName || "";
    const nicknameMatch = nickname
      .toString()
      .toLowerCase()
      .includes(filterNickname.toLowerCase());
    return accNoMatch && nicknameMatch;
  });

  if (loading) return <MainLoader show={loading} />;
  if (error)
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {error}
      </Alert>
    );
  if (data.length === 0)
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        No accounts found
      </Alert>
    );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ p: 2, borderRadius: 4, boxShadow: 3 }}>
      {/* Filter Section */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <TextField
          label="Filter Broadband Number"
          variant="outlined"
          size="small"
          value={filterAccNo}
          onChange={(e) => setFilterAccNo(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Filter Nickname"
          variant="outlined"
          size="small"
          value={filterNickname}
          onChange={(e) => setFilterNickname(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: "background.paper" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                Broadband Number
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                Nickname
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow
                key={item?.circuitId}
                sx={{
                  "&:nth-of-type(odd)": { bgcolor: "action.hover" },
                  "& .MuiTableCell-root": {
                    padding: "0px 5px",
                    lineHeight: 0.75,
                    height: "15px",
                  },
                }}
              >
                <TableCell sx={{ fontSize: 14 }}>{item.circuitId}</TableCell>
                <TableCell sx={{ fontSize: 14 }}>
                  {item?.circuitNickName || "-"}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenEdit(item)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Pagination */}
        <TablePagination
          sx={{
            "& .MuiTablePagination-root": {
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-end",
              minHeight: "56px",
              gap: "8px",
            },
            "& .MuiTablePagination-spacer": {
              order: 2,
            },
            "& .MuiTablePagination-selectLabel": {
              order: 0,
              display: "flex",
              alignItems: "baseline",
              padding: "15px",
              height: "36px",
            },
            "& .MuiTablePagination-select": {
              order: 1,
              display: "flex",
              alignItems: "center",
              height: "36px",
            },
            "& .MuiTablePagination-displayedRows": {
              order: 3,
              display: "flex",
              padding: "15px",
              alignItems: "baseline",
              height: "36px",
            },
            "& .MuiTablePagination-actions": {
              order: 4,
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
            },
          }}
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
            minWidth: 400,
            bgcolor: "background.paper",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: "bold",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            p: 2,
          }}
        >
          Edit Nickname
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Nickname"
            fullWidth
            variant="outlined"
            value={editedNickname}
            onChange={(e) => setEditedNickname(e.target.value)}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            bgcolor: "background.default",
            borderBottomLeftRadius: 3,
            borderBottomRightRadius: 3,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNickname}
            startIcon={<CheckIcon />}
            variant="contained"
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark",
              },
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error || success}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(false);
        }}
      >
        <Alert severity={success ? "success" : "error"} sx={{ width: "100%" }}>
          {success ? "Nickname saved successfully!" : error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default BroadbandFavourites;
